import type React from 'react';

import { AdornedInput, Adornment, FormField, Label, Paragraph } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../models/index';
import YesAndNoExpansion from '../../Widgets/YesAndNoExpansion';
import { INSUFFICIENT_FUNDS_FEE } from '../../../constants/index';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../constants/stringConstants';
import InlineTooltip from '../../Widgets/InlineTooltip';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function InsufficientFundsFee_v1({ section, setSectionValues }: Props): JSX.Element {
  function disableInsufficientFundsFee(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED]: false,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.AMOUNT]: '',
    });
  }

  function enableInsufficientFundsFee(): void {
    setSectionValues({
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED]: true,
      [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.AMOUNT]: '',
    });
  }

  function onAmountChange(event: React.FormEvent<HTMLInputElement>): void {
    const { value } = event.currentTarget;

    setSectionValues({ [INSUFFICIENT_FUNDS_FEE.FIELD_NAME.AMOUNT]: value });
  }

  const insufficientFundEnabled = section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.IS_ENABLED);
  return (
    <FormField
      label={
        <>
          <Label size="lg">
            Will you charge a fee if a payment gets{' '}
            <InlineTooltip
              tooltipBodyText="Reversals are usually caused by checks being returned for insufficient funds or by the tenant stopping or
          canceling a check. A fee charged for reversal of payment is different from a late fee."
            >
              reversed
            </InlineTooltip>
            ?
          </Label>
          <Paragraph marginBottom="sm">
            Reversals are commonly caused by payments being returned for insufficient funds.
          </Paragraph>
        </>
      }
      control={
        <YesAndNoExpansion
          yesHandler={enableInsufficientFundsFee}
          noHandler={disableInsufficientFundsFee}
          yesActive={insufficientFundEnabled}
        >
          <FormField
            label={<Label id="fee-amount">{INSUFFICIENT_FUNDS_FEE.LABEL.AMOUNT}</Label>}
            control={
              <AdornedInput
                inputId="AdornedInput-fee-amount"
                leftAdornment={<Adornment>$</Adornment>}
                rightAdornment={<Adornment>flat fee</Adornment>}
                input={
                  <CurrencyInput
                    aria-labelledby="fee-amount"
                    value={section.getFieldValue(INSUFFICIENT_FUNDS_FEE.FIELD_NAME.AMOUNT)}
                    onBlur={onAmountChange}
                    onChange={onAmountChange}
                    placeholder={CURRENCY_INPUT_PLACEHOLDER}
                  />
                }
              />
            }
          />
        </YesAndNoExpansion>
      }
    />
  );
}
